import React, { RefObject, ReactNode } from 'react';
import { StyleProp, ViewStyle, StyleSheet, View, Text, TouchableOpacity, Image } from 'react-native';
import { isMobile } from '@/utils/general/device-info';
import { mbApplyTransparency, mbPlatformStyle } from '@/utils/general/style-utils';
import { COLORS } from './colors';
import { MB_SVG_BrokenFile, MB_SVG_Play } from '@/components/general/svg-components';
import { MB_ImageObject } from '@/components/general/image';
import { MB_ORIENTATION_LOCK } from '@/utils/general/orientation-locker';

interface MB_VideoPlayerRef {
  /**
   * Play the video.
   * @param position The position to go to when playing in seconds.
   */
  play: (position?: number) => void;

  /**
   * Pause the video.
   */
  pause: () => void;

  /**
   * Stop the video.
   */
  stop: () => void;

  /**
   * Display the video in fullscreen.
   */
  presentFullscreenPlayer: () => void;

  /**
   * For some types of videos, only the audio is being loaded so we try
   * to detect it here. If it returns true, we can assume that there is
   * only audio present.
   */
  isInvisibleVideo: () => boolean;
}

interface MB_VideoObject {
  /**
   * Thumbnail of the video.
   */
  thumbnail?: MB_ImageObject;

  /**
   * The url of the video.
   */
  url: string;

  /**
   * Whether the video is currently being processed.
   */
  isProcessing: boolean;
}

enum MB_VideoPlayerPlayStatus {
  playing = 'playing',
  paused = 'paused',
  ended = 'ended',
  started = 'started',
}

enum MB_VIDEO_PLAYER_RESIZE_MODE {
  stretch = 'stretch',
  contain = 'contain',
  cover = 'cover',
  none = 'none',
}

interface MB_VideoPlayerProps {
  /**
   * A reference to the video player component. It can be used
   * to control the video player programmatically.
   */
  MB_Ref?: RefObject<MB_VideoPlayerRef | null>;

  /**
   * The source url of the video.
   */
  sourceUrl: string | undefined;

  /**
   * Show the video player controls.
   */
  showControls?: boolean;

  /**
   * If true, the video will start over again when it finishes.
   */
  isLooping?: boolean;

  /**
   * If true, the audio will be muted.
   */
  isMuted?: boolean;

  /**
   * If true, describes that the media is supposed to start playing
   * automatically.
   */
  shouldPlay?: boolean;

  /**
   * Callback to be called when the state of the native iOS fullscreen
   * view changes.
   */
  onFullscreenUpdate?: ((isFullScreen: boolean) => void) | undefined;

  /**
   * Callback to be called if load or playback have encountered a fatal
   * error.
   */
  onError?: ((error: any) => void) | undefined;

  /**
   * If true, displays a component indicating the video failed to be loaded.
   */
  failed?: boolean;

  /**
   * The message to be displayed when the video fails to load, i.e. when `failed`
   * is true.
   */
  failedMessage?: string;

  /**
   * The style of the video player container.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * If true, shows a preview image instead of the video player.
   */
  showPreview?: boolean;

  /**
   * The image to be displayed in the preview.
   */
  previewImageUrl?: string;

  /**
   * The style of the preview container.
   */
  previewContainerStyle?: StyleProp<ViewStyle>;

  /**
   * Callback to be called when the preview is pressed.
   */
  onPreviewPressed?: () => void;

  /**
   * Callback to be called when the video is ready for displayed.
   */
  onReady?: () => void;

  /**
   * Callback to be called when the video loaded.
   */
  onLoad?: () => void;

  /**
   * Callback to be called when the play status of the video changes.
   */
  onPlayStatusChanged?: (status: MB_VideoPlayerPlayStatus) => void;

  /**
   * How the video should be scaled for display in the component view
   * bounds.
   */
  resizeMode?: MB_VIDEO_PLAYER_RESIZE_MODE;

  /**
   * A boolean flag indicating whether the parent component is focused or not.
   * If isFocused is set to false, the component will just return null instead of returning the video component.
   * Useful in cases where switching from screen to screen in react navigation, the old screen does not
   * get unmounted. In those scenarios, the video will keep playing in the background.
   */
  isFocused?: boolean;

  /**
   * Sets the orientation back to the provided orientation when exiting the full screen.
   * orientation.restoreDefault is used if mobileOrientationRestore is not used.
   */
  mobileOrientationRestore?: MB_ORIENTATION_LOCK;

  /**
   * The component to be displayed while the video is loading.
   */
  loadingComponent?: ReactNode;

  /**
   * Callback fired as playback progresses (roughly every 500ms on mobile, on
   * every `timeupdate` on web). Durations are in milliseconds; `durationMillis`
   * is 0 until the media reports one.
   */
  onProgressUpdate?: (positionMillis: number, durationMillis: number) => void;
}

interface PreviewComponentProps {
  /**
   * The url of the preview image.
   */
  previewImageUrl: string | undefined;

  /**
   * The style of the preview container.
   */
  previewContainerStyle: StyleProp<ViewStyle>;

  /**
   * Callback to be called when the preview is pressed.
   */
  onPreviewPressed?: () => void;
}

const PreviewComponent = ({ previewImageUrl, previewContainerStyle, onPreviewPressed }: PreviewComponentProps) => {
  return (
    <TouchableOpacity style={isMobile ? [isMobile && styles.previewContainer, previewContainerStyle] : previewContainerStyle} onPress={onPreviewPressed}>
      {previewImageUrl ? (
        <Image style={{ width: '100%', height: '100%' }} source={{ uri: previewImageUrl }} />
      ) : (
        <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <MB_SVG_BrokenFile color={COLORS.white} size={32} />
          <Text style={styles.thumbnailNotAvailableText}>Thumbnail not available</Text>
        </View>
      )}

      <View
        style={[
          styles.playIconWrapper,
          {
            alignItems: previewImageUrl ? 'center' : 'flex-start',
            justifyContent: previewImageUrl ? 'center' : 'flex-start',
            paddingTop: previewImageUrl ? 0 : 8,
            paddingStart: previewImageUrl ? 0 : 8,
          },
        ]}
      >
        <MB_SVG_Play size={12} color={COLORS.white} containerStyle={styles.playIconContainer} />
      </View>
    </TouchableOpacity>
  );
};

export { MB_VideoPlayerPlayStatus, MB_VIDEO_PLAYER_RESIZE_MODE, PreviewComponent };
export type { MB_VideoPlayerProps, MB_VideoPlayerRef, MB_VideoObject };

const styles = StyleSheet.create({
  previewContainer: {
    ...mbPlatformStyle({
      mobile: {
        width: '100%',
        height: '100%',
      },
    }),
  },
  thumbnailNotAvailableText: {
    fontWeight: 'normal',
    fontSize: 12,
    color: COLORS.white,
    textAlign: 'center',
    marginTop: 4,
  },
  playIconWrapper: {
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
  playIconContainer: {
    width: isMobile ? 26 : 52.5,
    aspectRatio: 1,
    paddingStart: 2,
    borderRadius: 300,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: mbApplyTransparency(COLORS.black, 0.4),
    borderWidth: 1,
    borderColor: mbApplyTransparency(COLORS.white, 0.15),
  },
});
